import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DEFAULT_COMPANY } from 'app/app.constants';
import { EMPTY, from, Observable, Subscriber } from 'rxjs';
import { throwError } from 'rxjs/internal/observable/throwError';
import { catchError, concatMap, switchMap, takeWhile, tap } from 'rxjs/operators';

import { AppConfiguration } from '../../core/models/app-configuration.model';
import { getCompanyName } from '../util/request-util';
import { StateStorageService } from '../../core/auth/state-storage.service';

/**
 * Angular service, implements method for retrieving application config
 */
@Injectable({ providedIn: 'root' })
export class CompanyLogoService {
  constructor(private readonly http: HttpClient, private readonly appConfig: AppConfiguration, private readonly storageService: StateStorageService) { }

  /**
   * Fetch company image based on current url.
   * return an Image Observable
   */
  getCompanyImage(): Observable<string | ArrayBuffer> {
    const companyName = getCompanyName();

    if (
      !companyName ||
      companyName === DEFAULT_COMPANY ||
      !this.appConfig.companyLogo.url ||
      this.appConfig.companyLogo.extensions.length <= 0
    ) {
      return throwError('Load Failed');
    }

    let extensions = this.appConfig.companyLogo.extensions;
    const companyExtension = this.storageService.getCompanyLogoExtension();
    if (companyExtension) {
      extensions = [companyExtension];
    }

    const counter = extensions.length - 1;

    let imgObtained = false;
    return from(extensions).pipe(
      concatMap((sExtension, index) =>
        this.http
          .get(`${this.appConfig.companyLogo.url}/${companyName}/${this.appConfig.companyLogo.name}.${sExtension}`, {
            responseType: 'blob',
          })
          .pipe(
            catchError((error: HttpErrorResponse) => {
              if (index === counter) {
                return throwError(error);
              }
              return EMPTY;
            }),
            tap(() => {
              imgObtained = true;
            }),
            switchMap((logo: Blob) => {
              if (logo.type.includes('image')) {
                this.storageService.storeCompanyLogoExtension(sExtension);
                return this.loadImage(logo);
              }
              return new Observable((observer: Subscriber<string | ArrayBuffer>) => {
                observer.error();
                observer.complete();
              });
            })
          )
      ),
      takeWhile(() => imgObtained === false, true)
    );
  }

  /**
   * Try to load an image on client browser to use inside tag <img>
   * @param url Image url
   */
  private loadImage(url: Blob): Observable<string | ArrayBuffer> {
    return new Observable((observer: Subscriber<string | ArrayBuffer>) => {
      const reader = new FileReader();
      reader.readAsDataURL(url);
      reader.onload = () => {
        if (reader.result) {
          observer.next(reader.result);
        } else {
          observer.error();
        }
        observer.complete();
      };
    });
  }
}
